import GUI from 'lil-gui';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
// ✅ ----- Import the fond and font loader -----
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js';

/**
 * Base
 */
// Debug
const gui = new GUI();

// Canvas
const canvas = document.querySelector('canvas.webgl');

// Scene
const scene = new THREE.Scene();

// ✅ ----- Add axis helper to find center of scene -----
// const axesHelper = new THREE.AxesHelper();
// scene.add(axesHelper);

/**
 * Textures
 */
const textureLoader = new THREE.TextureLoader();

// Load all matcap textures
const matcapTextures = [];
for (let i = 1; i <= 8; i++) {
	matcapTextures.push(textureLoader.load(`/textures/matcaps/${i}.png`));
}

// Parameters for debug UI
const parameters = {
	matcapTexture: 0, // Default to first texture (index 0)
};

// Create material with initial matcap texture
const material = new THREE.MeshMatcapMaterial({
	matcap: matcapTextures[parameters.matcapTexture],
});

// Add debug UI for matcap textures
gui
	.add(parameters, 'matcapTexture', {
		'Matcap 1': 0,
		'Matcap 2': 1,
		'Matcap 3': 2,
		'Matcap 4': 3,
		'Matcap 5': 4,
		'Matcap 6': 5,
		'Matcap 7': 6,
		'Matcap 8': 7,
	})
	.name('Matcap Texture')
	.onChange(() => {
		material.matcap = matcapTextures[parameters.matcapTexture];
		material.needsUpdate = true;
	});

// ✅ ----- Load font -----
// One font loader can load multiple fonts
const fontLoader = new FontLoader();

fontLoader.load('/fonts/helvetiker_regular.typeface.json', (font) => {
	const textGeometry = new TextGeometry('Hello World', {
		font: font,
		size: 0.5,
		height: 0.2,
		curveSegments: 5,
		bevelEnabled: true,
		bevelThickness: 0.03,
		bevelSize: 0.02,
		bevelOffset: 0,
		bevelSegments: 4,
	});

	// ✅ ----- Center text using bounding box -----
	// We also need to remove bevel size and thickness to center it
	textGeometry.computeBoundingBox();
	// textGeometry.translate(
	// 	-(textGeometry.boundingBox.max.x - 0.02) * 0.5,
	// 	-(textGeometry.boundingBox.max.y - 0.02) * 0.5,
	// 	-(textGeometry.boundingBox.max.z - 0.03) * 0.5
	// );

	// Or use the center method
	textGeometry.center();

	const text = new THREE.Mesh(textGeometry, material);
	scene.add(text);

	// ✅ ----- Place create material and geometry outside of loop for better performance -----
	const donutGeometry = new THREE.TorusGeometry(0.3, 0.2, 20, 45);

	// ✅ ----- Create 100 donuts -----
	for (let i = 0; i < 250; i++) {
		const donut = new THREE.Mesh(donutGeometry, material);

		// ✅ ----- Add randomness to position -----
		donut.position.x = (Math.random() - 0.5) * 10;
		donut.position.y = (Math.random() - 0.5) * 10;
		donut.position.z = (Math.random() - 0.5) * 10;

		// ✅ ----- Add randomness to rotation -----
		donut.rotation.x = Math.random() * Math.PI;
		donut.rotation.y = Math.random() * Math.PI;

		// ✅ ----- Add scale randomness -----
		const randomScaleValue = Math.random() + 0.1;
		donut.scale.set(randomScaleValue, randomScaleValue, randomScaleValue);

		scene.add(donut);
	}
});

/**
 * Sizes
 */
const sizes = {
	width: window.innerWidth,
	height: window.innerHeight,
};

window.addEventListener('resize', () => {
	// Update sizes
	sizes.width = window.innerWidth;
	sizes.height = window.innerHeight;

	// Update camera
	camera.aspect = sizes.width / sizes.height;
	camera.updateProjectionMatrix();

	// Update renderer
	renderer.setSize(sizes.width, sizes.height);
	renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
});

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(
	75,
	sizes.width / sizes.height,
	0.1,
	100
);
camera.position.x = 1;
camera.position.y = 1;
camera.position.z = 2;
scene.add(camera);

// Controls
const controls = new OrbitControls(camera, canvas);
controls.enableDamping = true;

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
	canvas: canvas,
});
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

/**
 * Animate
 */
const clock = new THREE.Clock();

const tick = () => {
	const elapsedTime = clock.getElapsedTime();

	// Update controls
	controls.update();

	// Render
	renderer.render(scene, camera);

	// Call tick again on the next frame
	window.requestAnimationFrame(tick);
};

tick();
